package dev.lingvo.shared

/** One character a translator can insert, with a label saying what it is for. */
data class PunctuationKey(
    val character: String,
    val label: String,
    /** Shown on hover. Says *when* to use it, which is the part nobody remembers. */
    val hint: String,
)

/** The quotation marks a locale expects, as an opening and closing pair. */
data class QuoteStyle(val open: String, val close: String)

/** Narrow no-break space: French typography, before `; : ! ?` and inside guillemets. */
const val NARROW_NO_BREAK_SPACE = " "

/**
 * Per-locale punctuation, for a strip above the target field.
 *
 * These are characters a US keyboard cannot produce. Today there is no way to type them and no check
 * that catches a straight `"` in a locale that forbids it, so every French and German translation
 * arrives typographically wrong and nobody finds out until a native speaker reads the shipped app.
 *
 * The list is deliberately short. A palette of every Unicode punctuation mark is a palette nobody
 * scans; six characters that a translator in *this* locale actually needs is a strip they use.
 */
fun punctuationFor(locale: String?): List<PunctuationKey> {
    val language = locale?.trim()?.replace('_', '-')?.substringBefore('-')?.lowercase()
    val quotes = quoteStyleFor(locale)
    val common = listOf(
        PunctuationKey("…", "Ellipsis", "One character, not three dots — it kerns and wraps correctly."),
        PunctuationKey("’", "Apostrophe", "The typographic apostrophe, not the straight quote."),
    )
    val quoteKeys = listOf(
        PunctuationKey(quotes.open, "Open quote", "The opening quotation mark for this locale."),
        PunctuationKey(quotes.close, "Close quote", "The closing quotation mark for this locale."),
    )
    return when (language) {
        // French puts a narrow no-break space before its high punctuation and inside guillemets.
        // A plain space breaks across lines and leaves the punctuation mark alone at the start of
        // the next one.
        "fr" -> quoteKeys + listOf(
            PunctuationKey(
                NARROW_NO_BREAK_SPACE,
                "Narrow no-break space",
                "Before ; : ! ? and inside « », so the punctuation cannot wrap to the next line.",
            ),
        ) + common
        else -> quoteKeys + common
    }
}

/**
 * Which quotation marks a locale uses.
 *
 * Getting this wrong is not cosmetic in German or Polish: `„ "` and `" "` look like different marks
 * to a reader, and straight `"` looks like a developer forgot.
 */
fun quoteStyleFor(locale: String?): QuoteStyle {
    val language = locale?.trim()?.replace('_', '-')?.substringBefore('-')?.lowercase()
    return when (language) {
        // `ka` (Georgian) is mapped although the locale picker does not offer it. Kept rather than
        // pruned: the API and CLI accept any valid BCP-47 tag, so a project can carry a locale the
        // picker has no row for, and the mapping costs one token in a `when`.
        "de", "cs", "sk", "sl", "lt", "hr", "sr", "bg", "ka", "is", "et" -> QuoteStyle("„", "“")
        "pl", "hu", "ro" -> QuoteStyle("„", "”")
        // `nb` alongside `no`: the picker offers both Norwegian and Norwegian Bokmål as separate
        // entries, so without it Bokmål fell through to the English pair while Norwegian got
        // guillemets — one language, two answers, decided by which row a customer happened to click.
        "fr", "ru", "uk", "es", "it", "pt", "el", "no", "nb", "tr", "ca" -> QuoteStyle("«", "»")
        "da" -> QuoteStyle("»", "«")
        "fi", "sv" -> QuoteStyle("”", "”")
        "ja", "zh" -> QuoteStyle("「", "」")
        else -> QuoteStyle("“", "”")
    }
}

/**
 * Whether a translation uses straight quotes where its locale expects typographic ones.
 *
 * A **warning**, never an error. A straight quote inside a code sample or a measurement (`5" pipe`)
 * is correct, and blocking approval over punctuation would teach people to ignore the QA panel —
 * which is where the errors that do matter live.
 *
 * Only fires when the source did not have them either. A source string containing `"` is usually
 * quoting something literal, and demanding the translation change it would be wrong.
 */
fun usesStraightQuotes(source: String?, target: String?, locale: String?): Boolean {
    if (target.isNullOrBlank()) return false
    // A locale whose own style *is* the straight-ish pair has nothing to complain about.
    val style = quoteStyleFor(locale)
    if (style.open == "\"" || style.close == "\"") return false
    val targetHasStraight = target.contains('"')
    if (!targetHasStraight) return false
    // The source set the precedent: leave it alone.
    if (source?.contains('"') == true) return false
    return true
}
